/**
 * Merges structure + progress + assignment term_summary into the legacy Term[]
 * and derives selectedTerm/selectedWeek from the URL. Structure and progress are
 * fetched by StudentDataContext and passed in.
 */

import { useState, useEffect, useMemo } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type { Term, Week } from "@ts/interfaces/terms/api/termsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import type { GradeStructureApiResponse } from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import type { GradeProgressApiResponse } from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import type { AssignmentTermSummary } from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import { mergeTerms } from "./mergeTerms";

interface UseTermsDataParams {
  urlTermId: string | undefined;
  urlWeekId: string | undefined;
  structure: GradeStructureApiResponse | null;
  progress: GradeProgressApiResponse | null;
  termSummary: Record<string, AssignmentTermSummary> | null;
  assignmentsUrl: string;
  structureStatus: REQUEST_STATUS;
  structureError: ErrorInterface | null;
  forGradeCode: number | undefined;
  isReady: boolean;
}

interface UseTermsDataReturn {
  terms: Term[];
  termsStatus: REQUEST_STATUS;
  termsError: ErrorInterface | null;
  selectedTerm: Term | null;
  selectedWeek: Week | null;
  setSelectedTerm: (term: Term | null) => void;
  setSelectedWeek: (week: Week | null) => void;
  forGradeCode: number | undefined;
  isReady: boolean;
}

export function useTermsData({
  urlTermId,
  urlWeekId,
  structure,
  progress,
  termSummary,
  assignmentsUrl,
  structureStatus,
  structureError,
  forGradeCode,
  isReady
}: UseTermsDataParams): UseTermsDataReturn {
  const [manualTerm, setManualTerm] = useState<Term | null>(null);
  const [manualWeek, setManualWeek] = useState<Week | null>(null);

  const terms = useMemo(
    () => mergeTerms(structure, progress, termSummary, assignmentsUrl),
    [structure, progress, termSummary, assignmentsUrl]
  );

  useEffect(() => {
    setManualTerm(null);
    setManualWeek(null);
  }, [urlTermId, urlWeekId]);

  // Return current data while a background refetch runs; nulling the selection
  // mid-refetch unmounts the lesson layout and restarts start/complete. BLD2-523.
  const selectedTerm = useMemo(() => {
    if (manualTerm) return manualTerm;
    if (!urlTermId) return null;
    if (terms.length === 0) return null;
    return terms.find(t => t.id === urlTermId) ?? null;
  }, [manualTerm, urlTermId, terms]);

  const selectedWeek = useMemo(() => {
    if (manualWeek) return manualWeek;
    if (!urlWeekId || !selectedTerm) return null;
    return selectedTerm.weeks.find(w => w.id === urlWeekId) ?? null;
  }, [manualWeek, urlWeekId, selectedTerm]);

  return {
    terms,
    termsStatus: structureStatus,
    termsError: structureError,
    selectedTerm,
    selectedWeek,
    setSelectedTerm: setManualTerm,
    setSelectedWeek: setManualWeek,
    forGradeCode,
    isReady
  };
}
